import { styled } from "@pigment-css/react";
import type { OrderPulseHero as OrderPulseHeroSchema, PulseStatus } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { daysSince } from "@acto/taskpane/field-sales-2/utils/format";
import {
  Card,
  Footer,
  FooterCol,
  FooterValue,
  Header,
  CardHeadline,
  MetaLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

const Grid = styled("div")(({ theme }) => ({
  display: "grid",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  height: theme.fieldSalesTwo.spacing[32],
}));

const Dot = styled("div")<{ status: PulseStatus }>(({ theme }) => ({
  borderRadius: theme.fieldSalesTwo.radius.circular,
  placeSelf: "center",
  variants: [
    {
      props: { status: "active" },
      style: {
        width: 10,
        height: 10,
        background: theme.fieldSalesTwo.colors.neutral[900],
      },
    },
    {
      props: { status: "silent" },
      style: {
        width: 3,
        height: 3,
        background: theme.fieldSalesTwo.colors.neutral[300],
      },
    },
  ],
}));

interface OrderPulseHeroProps {
  part: OrderPulseHeroSchema;
}

export function OrderPulseHero({ part }: OrderPulseHeroProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderPulse");

  const silentDays = daysSince(part.lastOrder);

  const headlineText = t("silenceHeadline", { days: silentDays });

  const normalCycleLabel =
    part.normalCycle.minDays === part.normalCycle.maxDays
      ? t("normalCycleSingle", { days: part.normalCycle.minDays })
      : t("normalCycleRange", {
          min: part.normalCycle.minDays,
          max: part.normalCycle.maxDays,
        });

  const rangeLabel = t("rangeValue", { weeks: part.weeks.length });

  return (
    <Card>
      <Header>
        <MetaLabel>{t("meta")}</MetaLabel>
        <CardHeadline>{headlineText}</CardHeadline>
      </Header>
      <Grid
        style={{
          gridTemplateColumns: `repeat(${part.weeks.length}, minmax(0, 1fr))`,
        }}
      >
        {part.weeks.map((status, index) => (
          <Dot key={index} status={status} />
        ))}
      </Grid>
      <Divider />
      <Footer>
        <FooterCol>
          <MetaLabel>{t("normalLabel")}</MetaLabel>
          <FooterValue>{normalCycleLabel}</FooterValue>
        </FooterCol>
        <FooterCol>
          <MetaLabel>{t("rangeLabel")}</MetaLabel>
          <FooterValue>{rangeLabel}</FooterValue>
        </FooterCol>
      </Footer>
    </Card>
  );
}
